
import React from "react";
import { useNavigate } from "react-router-dom";
import useModuleProgress from "../hooks/useModuleProgress";
import "../styles/pages/_module-pages.scss";
import "../styles/pages/_learn-assessments.scss";
import "../styles/pages/_quiz-completion.scss";

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: "Understanding Work-Life Balance",
    content: [
      "<strong>Learning Goal:</strong> Find harmony between your professional responsibilities and personal life to reduce stress and improve well-being.",
      "<br/>",
      "<strong>Key Topics:</strong>",
      "• What is work-life balance?",
      "• Benefits of balance for health and productivity",
      "• Common challenges",
      "<br/>",
      "<strong>Discussion:</strong> Work-life balance means making time for both work and personal life. It helps you feel happier and more productive."
    ]
  },
  {
    title: "Tips for Achieving Balance",
    content: [
      "Practical ways to create balance:",
      "<br/>",
      "• Set clear boundaries between work and personal time",
      "• Take regular breaks and use your leave days",
      "• Prioritize tasks and delegate when possible",
      "• Make time for hobbies, family, and relaxation",
      "<br/>",
      "<strong>Mini Practice:</strong> List one thing you can do this week to improve your work-life balance."
    ]
  },
  {
    title: "Reflection",
    content: [
      "Think about your current routine:",
      "• Are you giving enough time to both work and personal life?",
      "• What changes can you make?",
      "<br/>",
      "<strong>Journal Prompt:</strong> 'Today, I balanced my work and life by…'"
    ]
  },
  {
    title: "Ready to Test Your Knowledge?",
    content: [
      "You've completed Module 1: Work-Life Balance! Now, let's reinforce your learning with a quick quiz."
    ],
    additionalSection: {
      title: "Next Steps",
      content: [
        "Click 'Take the Quiz' below to test your knowledge about work-life balance.",
        "The quiz will help you review key concepts about balancing work and personal life."
      ]
    }
  }
];

const OccupationalModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress("occupationalM1");
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
  navigate("/panel/occupational-module-1/quiz");
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate("/panel/occupational-resources");
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left occupational-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Work-Life Balance</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Find harmony between your professional responsibilities and personal life to reduce stress and improve well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? "Exit Module" : "Previous"}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate("/panel/occupational-module-1/quiz")}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule1Page;


